'use client'
import { logger } from '@/lib/logger';

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { createSWS_Error, ErrorType, ErrorSeverity } from '@/lib/error-handling'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  name?: string // Component name for better error tracking
  showDetails?: boolean // Show detailed error information
  recovery?: boolean // Enable recovery options
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  retryCount: number
}

class ErrorBoundary extends Component<Props, State> {
  private maxRetries = 3
  private lastError?: ReturnType<typeof createSWS_Error>

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      retryCount: 0
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      retryCount: 0
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    this.props.onError?.(error, errorInfo)

    // Classify error type and severity
    const errorType = this.classifyError(error)
    const severity = this.determineSeverity(error, errorInfo)

    // Create standardized SWS error
    const swsError = createSWS_Error(errorType, severity, error.message, {
      originalError: error,
      context: {
        component: this.props.name || 'Unknown',
        componentStack: errorInfo.componentStack,
        retryCount: this.state.retryCount
      },
      recoverable: this.props.recovery !== false && this.state.retryCount < this.maxRetries,
      recoveryStrategies: this.getRecoveryStrategies(errorType)
    })

    // Enhanced error logging with SWS error system
    if (process.env.NODE_ENV === 'development') {
      logger.group(`🚨 Error Boundary: ${this.props.name || 'Unknown Component'}`)
      logger.error('SWS Error:', swsError)
      logger.error('Component Stack:', errorInfo.componentStack)
      logger.groupEnd()
    }

    // Store error for potential reporting
    this.lastError = swsError

    // Could send to error reporting service here
    // this.reportError(swsError)
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prev => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prev.retryCount + 1
      }))
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: 0
    })
  }

  private classifyError(error: Error): ErrorType {
    const message = error.message.toLowerCase()

    if (message.includes('webgl') || message.includes('webgpu') || message.includes('canvas')) {
      return ErrorType.RENDERING
    }
    if (message.includes('audio') || message.includes('webaudio') || message.includes('audiocontext')) {
      return ErrorType.WEB_AUDIO_API
    }
    if (message.includes('network') || message.includes('fetch') || message.includes('xhr')) {
      return ErrorType.NETWORK
    }
    if (message.includes('memory') || message.includes('out of memory')) {
      return ErrorType.MEMORY
    }
    if (message.includes('chunk') || message.includes('loading') || message.includes('module')) {
      return ErrorType.UNKNOWN // Could be network or bundling issue
    }

    return ErrorType.UNKNOWN
  }

  private determineSeverity(error: Error, errorInfo: ErrorInfo): ErrorSeverity {
    // Check if it's a critical rendering error
    if (error.message.toLowerCase().includes('webgpu') ||
        error.message.toLowerCase().includes('webgl context lost')) {
      return ErrorSeverity.CRITICAL
    }

    // Check component stack depth (deeper stack might indicate more severe issues)
    const componentStack = errorInfo.componentStack || ''
    const componentCount = (componentStack.match(/in\s+\w+/g) || []).length

    if (componentCount > 10) {
      return ErrorSeverity.HIGH
    }

    return ErrorSeverity.MEDIUM
  }

  private getRecoveryStrategies(errorType: ErrorType) {
    const strategies = []

    switch (errorType) {
      case ErrorType.RENDERING:
        strategies.push({
          id: 'fallback_renderer',
          name: 'Switch to Canvas 2D',
          description: 'Fallback to software rendering',
          execute: () => {
            // Could set a flag to use canvas 2d instead of webgpu
            logger.log('Switching to fallback renderer')
          },
          priority: 1
        })
        break

      case ErrorType.WEB_AUDIO_API:
        strategies.push({
          id: 'reset_audio_context',
          name: 'Reset Audio Context',
          description: 'Reinitialize audio context',
          execute: () => {
            // Could call audio context reset
            logger.log('Resetting audio context')
          },
          priority: 1
        })
        break

      case ErrorType.NETWORK:
        strategies.push({
          id: 'retry_with_backoff',
          name: 'Retry with Backoff',
          description: 'Retry network request with exponential backoff',
          execute: () => {
            logger.log('Retrying with backoff')
          },
          priority: 1
        })
        break
    }

    // Always include basic retry
    strategies.push({
      id: 'basic_retry',
      name: 'Basic Retry',
      description: 'Attempt to reload the component',
      execute: () => this.handleRetry(),
      priority: 2
    })

    return strategies
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const canRetry = this.props.recovery !== false && this.state.retryCount < this.maxRetries
      const errorId = `error-${this.props.name || 'unknown'}-${Date.now()}`

      return (
        <div
          className="min-h-[200px] flex items-center justify-center p-6 bg-red-900/20 border border-red-500/30 rounded-lg"
          role="alert"
          aria-labelledby={`${errorId}-title`}
          aria-describedby={`${errorId}-description`}
        >
          <div className="text-center max-w-md">
            <div className="text-red-400 text-4xl mb-4" aria-hidden="true">⚠️</div>
            <h3
              id={`${errorId}-title`}
              className="text-red-300 font-semibold mb-2"
            >
              {this.props.name ? `${this.props.name} Error` : 'Something went wrong'}
            </h3>
            <p
              id={`${errorId}-description`}
              className="text-red-200/80 text-sm mb-4"
            >
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>

            {this.props.showDetails && this.state.error?.stack && (
              <details className="mb-4 text-left">
                <summary className="text-red-300 cursor-pointer text-sm mb-2">
                  Technical Details
                </summary>
                <pre className="text-red-200/60 text-xs bg-red-950/30 p-2 rounded overflow-auto max-h-32">
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            <div className="flex gap-2 justify-center">
              {canRetry && (
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                  aria-label={`Retry loading ${this.props.name || 'component'} (attempt ${this.state.retryCount + 1}/${this.maxRetries + 1})`}
                >
                  Try Again ({this.state.retryCount + 1}/{this.maxRetries + 1})
                </button>
              )}

              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
                aria-label="Reset and reload component"
              >
                Reset
              </button>
            </div>

            {this.state.retryCount >= this.maxRetries && (
              <p className="text-red-300/60 text-xs mt-3">
                Maximum retry attempts reached. Please refresh the page.
              </p>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary